import { useCallback, useEffect, useMemo, useState } from "react";
import { Check, Loader2, MapPin, AlertCircle, Pencil, Ban, Trash2, Navigation } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { SinglePhotoSlot } from "./SinglePhotoSlot";
import { FieldInfoButton } from "./FieldInfoButton";
import { MapperMap } from "./MapperMap";
import { GpsAccuracyModal } from "./GpsAccuracyModal";
import { PIL_FIELD_HELP } from "./mappingHelpContent";
import type { UploadedPhoto } from "./PhotoCapture";
import type { UserPosition } from "@/hooks/useUserPosition";
import { trpc } from "@/lib/trpc";
import { cn } from "@/lib/utils";

type SaveState = "idle" | "saving" | "saved" | "error";

export interface PILPhoto {
  id: string;
  kind: "overview" | "degradation" | "pole_wide" | "pole_side" | "pole_base" | string;
  url: string;
}

export interface PILData {
  id: string;
  ordinal: number;
  status: string;
  lat: number;
  lng: number;
  is_viable: boolean | null;
  general_comments: string | null;
  placed_by: "admin" | "mapper" | null;
  rejection_reason: string | null;
  rejected_at: string | null;
  photos: PILPhoto[];
}

interface PILFormSheetProps {
  token: string;
  pil: PILData;
  onChanged?: () => void;
  className?: string;
  locked?: boolean;
  /** Live user GPS position for the embedded minimap. Lifted from
   *  MappingLocationPage so we don't run two `watchPosition` listeners. */
  userPos?: UserPosition | null;
}

function hasGpsCoords(p: { lat: number; lng: number }): boolean {
  return p.lat !== 0 || p.lng !== 0;
}

function SaveIndicator({ state }: { state: SaveState }) {
  if (state === "saving") {
    return (
      <span className="inline-flex items-center gap-1 text-xs text-muted-foreground">
        <Loader2 className="h-3 w-3 animate-spin" />
        Saving…
      </span>
    );
  }
  if (state === "saved") {
    return (
      <span className="inline-flex items-center gap-1 text-xs text-green-600">
        <Check className="h-3 w-3" />
        Saved
      </span>
    );
  }
  if (state === "error") {
    return (
      <span className="inline-flex items-center gap-1 text-xs text-destructive">
        <AlertCircle className="h-3 w-3" />
        Save failed
      </span>
    );
  }
  return null;
}

export function PILFormSheet({
  token,
  pil,
  onChanged,
  className,
  locked,
  userPos,
}: PILFormSheetProps) {
  const [local, setLocal] = useState(pil);
  const [saveState, setSaveState] = useState<SaveState>("idle");
  const [gpsModalOpen, setGpsModalOpen] = useState(false);
  // Seed coords used when the GPS modal opens. Updated by the "Update GPS"
  // button after a fresh getCurrentPosition() reading.
  const [gpsModalSeed, setGpsModalSeed] = useState<{ lat: number; lng: number } | null>(null);
  const [gettingFreshGps, setGettingFreshGps] = useState(false);

  useEffect(() => {
    setLocal(pil);
  }, [pil]);

  const utils = trpc.useUtils?.();

  const updateAnswers = trpc.share.updatePILAnswers.useMutation({
    onMutate: () => setSaveState("saving"),
    onSuccess: () => {
      setSaveState("saved");
      onChanged?.();
      setTimeout(() => setSaveState((s) => (s === "saved" ? "idle" : s)), 1500);
    },
    onError: (err) => {
      setSaveState("error");
      toast.error(`Failed to save: ${err.message}`);
    },
  });

  const updateGps = trpc.share.updatePILGps.useMutation({
    onMutate: () => setSaveState("saving"),
    onSuccess: () => {
      setSaveState("saved");
      onChanged?.();
      setTimeout(() => setSaveState((s) => (s === "saved" ? "idle" : s)), 1500);
    },
    onError: (err) => {
      setSaveState("error");
      toast.error(`Failed to save GPS: ${err.message}`);
    },
  });

  const uploadPolePhoto = trpc.share.uploadPolePhoto.useMutation({
    onSuccess: () => onChanged?.(),
    onError: (err) => toast.error(`Photo upload failed: ${err.message}`),
  });

  const deletePolePhoto = trpc.share.deletePolePhoto.useMutation({
    onSuccess: () => onChanged?.(),
    onError: (err) => toast.error(`Failed to delete photo: ${err.message}`),
  });

  const markDone = trpc.share.markPILDone.useMutation({
    onSuccess: () => {
      toast.success(`Installation spot ${local.ordinal} marked done`);
      onChanged?.();
      utils?.share?.listMappingLocations?.invalidate?.();
    },
    onError: (err) => toast.error(err.message),
  });

  const reopen = trpc.share.reopenPIL.useMutation({
    onSuccess: () => {
      onChanged?.();
      utils?.share?.listMappingLocations?.invalidate?.();
    },
    onError: (err) => toast.error(err.message),
  });

  const reject = trpc.share.rejectPIL.useMutation({
    onSuccess: () => {
      toast.success(`Installation spot ${local.ordinal} rejected`);
      onChanged?.();
      utils?.share?.listMappingLocations?.invalidate?.();
    },
    onError: (err) => toast.error(err.message),
  });

  const deleteMapperPIL = trpc.share.deleteMapperPIL.useMutation({
    onSuccess: () => {
      toast.success(`Installation spot ${local.ordinal} deleted`);
      onChanged?.();
      utils?.share?.listMappingLocations?.invalidate?.();
    },
    onError: (err) => toast.error(err.message),
  });

  const [rejectOpen, setRejectOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [rejectReason, setRejectReason] = useState("");

  const commit = useCallback(
    (patch: Partial<PILData>) => {
      updateAnswers.mutate({
        token,
        pilId: local.id,
        patch: patch as any,
      });
    },
    [token, local.id, updateAnswers],
  );

  // 2-step "Update GPS" flow:
  //   1. Take a fresh fix to seed the modal map. Try high-accuracy first
  //      (gives device GPS on phones); fall back silently to low-accuracy
  //      (network/IP) if it times out, since the modal still works fine
  //      with the seeded position the user can pan.
  //   2. Open the modal — the user pans under a fixed crosshair, then
  //      "Confirm" saves `map.getCenter()` (NOT the GPS reading).
  const handleStartGpsFlow = useCallback(() => {
    setGettingFreshGps(true);
    const fallbackSeed = hasGpsCoords(local)
      ? { lat: local.lat, lng: local.lng }
      : { lat: pil.lat, lng: pil.lng };

    if (!navigator.geolocation) {
      // No geolocation API — open with whatever coords we already have.
      setGpsModalSeed(fallbackSeed);
      setGpsModalOpen(true);
      setGettingFreshGps(false);
      return;
    }

    const seedFromPos = (pos: GeolocationPosition) => {
      setGettingFreshGps(false);
      setGpsModalSeed({
        lat: pos.coords.latitude,
        lng: pos.coords.longitude,
      });
      setGpsModalOpen(true);
    };

    // First attempt: high-accuracy. Phones use the GPS chip; can take longer.
    navigator.geolocation.getCurrentPosition(
      seedFromPos,
      () => {
        // High-accuracy failed (usually a timeout on desktop or a weak
        // GPS lock indoors). Try once more with low-accuracy — that's
        // typically near-instant via wifi/IP. Open with the fallback
        // seed if even that fails. No error toast: the modal lets the
        // user pan to the right spot anyway.
        navigator.geolocation.getCurrentPosition(
          seedFromPos,
          () => {
            setGettingFreshGps(false);
            setGpsModalSeed(fallbackSeed);
            setGpsModalOpen(true);
          },
          { enableHighAccuracy: false, timeout: 8_000, maximumAge: 60_000 },
        );
      },
      { enableHighAccuracy: true, timeout: 15_000, maximumAge: 10_000 },
    );
    // intentionally no deps on `pil` lat/lng beyond initial: closure is fine
    // here because we only ever read the latest values inside the callback.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [local.lat, local.lng, pil.lat, pil.lng]);

  const handleConfirmGps = useCallback(
    (lat: number, lng: number) => {
      setLocal((l) => ({ ...l, lat, lng }));
      updateGps.mutate({ token, pilId: local.id, lat, lng });
      setGpsModalOpen(false);
    },
    [token, local.id, updateGps],
  );

  const poleWidePhoto = useMemo(
    () => (local.photos.find((p) => p.kind === "pole_wide") as UploadedPhoto | undefined) ?? null,
    [local.photos],
  );
  const poleSidePhoto = useMemo(
    () => (local.photos.find((p) => p.kind === "pole_side") as UploadedPhoto | undefined) ?? null,
    [local.photos],
  );
  const poleBasePhoto = useMemo(
    () => (local.photos.find((p) => p.kind === "pole_base") as UploadedPhoto | undefined) ?? null,
    [local.photos],
  );

  const handlePoleUpload = useCallback(
    async (kind: "pole_wide" | "pole_side" | "pole_base", base64: string) => {
      await uploadPolePhoto.mutateAsync({ token, pilId: local.id, kind, base64 });
    },
    [token, local.id, uploadPolePhoto],
  );

  const handlePoleDelete = useCallback(
    async (photoId: string) => {
      await deletePolePhoto.mutateAsync({ token, pilId: local.id, photoId });
    },
    [token, local.id, deletePolePhoto],
  );

  const hasGps = local.lat !== 0 || local.lng !== 0;
  const hasAllPolePhotos = !!poleWidePhoto && !!poleSidePhoto && !!poleBasePhoto;
  const canMarkDone = hasGps && hasAllPolePhotos && local.status !== "done";

  const isDone = local.status === "done";
  const isRejected = local.status === "rejected";
  const canReject = local.placed_by === "admin" && !isDone && !isRejected;
  const canDelete = local.placed_by === "mapper" && !isRejected;

  if (locked) {
    return (
      <div className={cn("space-y-3 px-4 pb-6", className)}>
        <div className="rounded-md border border-amber-300 bg-amber-50 p-3 space-y-1">
          <p className="text-sm font-semibold text-amber-900">
            Upload 3 overview photos of this location before editing spots.
          </p>
          <p className="text-xs text-amber-900/80">
            Scroll to "Overview photos" above to add them. This spot will unlock automatically.
          </p>
        </div>
      </div>
    );
  }

  if (isRejected) {
    return (
      <div className={cn("space-y-3 px-4 pb-6", className)}>
        <div className="rounded-md border border-gray-300 bg-gray-50 p-3 space-y-3">
          <div className="space-y-1">
            <p className="text-sm font-semibold text-gray-800">This spot was rejected</p>
            {local.rejection_reason ? (
              <p className="text-xs text-gray-600">{local.rejection_reason}</p>
            ) : (
              <p className="text-xs text-gray-500 italic">No reason provided</p>
            )}
          </div>
          <Button
            type="button"
            variant="outline"
            onClick={() => reopen.mutate({ token, pilId: local.id })}
            disabled={reopen.isPending}
            className="gap-2"
          >
            {reopen.isPending ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <Pencil className="h-4 w-4" />
            )}
            Un-reject
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className={cn("space-y-3 px-4 pb-6", className)}>
      <div className="flex items-center justify-end sticky top-0 bg-background/95 backdrop-blur pb-2 -mx-4 px-4 pt-1 z-10">
        <SaveIndicator state={saveState} />
      </div>

      {/* 1. GPS */}
      <section className="space-y-2">
        <div className="flex items-center gap-1.5">
          <Label className="text-sm font-semibold">1. GPS location</Label>
          <FieldInfoButton {...PIL_FIELD_HELP.gpsLocation} />
        </div>
        <p className="text-xs text-muted-foreground">
          Stand where you want the installation spot, then tap "Update GPS".
          Pan the next map so the red pin sits exactly on the mounting point.
        </p>
        {hasGps && (
          <div className="rounded-md border bg-muted/40 p-2 text-xs text-muted-foreground space-y-2">
            <div className="tabular-nums">
              {local.lat.toFixed(6)}, {local.lng.toFixed(6)}
            </div>
            {/* No `key` here — re-mounting on every coordinate change wipes
                the user's pan/zoom state and is the root cause of the
                feedback "the minimap jumps every time". The new
                `showRecenterOnTarget` button lets the mapper snap back. */}
            <div className="relative">
              <MapperMap
                className="h-40 w-full rounded overflow-hidden"
                defaultCenter={{ lat: local.lat, lng: local.lng }}
                defaultZoom={20}
                pils={[
                  {
                    id: local.id,
                    lat: local.lat,
                    lng: local.lng,
                    status: local.status,
                    ordinal: local.ordinal,
                  },
                ]}
                selectedPilId={local.id}
                userPos={userPos ?? null}
                showRecenterOnTarget
              />
            </div>
            <a
              href={`https://www.google.com/maps/dir/?api=1&destination=${local.lat},${local.lng}&travelmode=walking`}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex min-h-9 items-center gap-1.5 rounded-md border bg-background px-2.5 py-1.5 text-xs font-medium text-foreground hover:bg-accent"
            >
              <Navigation className="h-3.5 w-3.5" />
              Navigate to this spot
            </a>
          </div>
        )}
        <Button
          type="button"
          onClick={handleStartGpsFlow}
          className="w-full min-h-11 gap-2 bg-blue-500 text-white hover:bg-blue-600"
          disabled={updateGps.isPending || gettingFreshGps || isDone}
        >
          {gettingFreshGps ? (
            <Loader2 className="h-4 w-4 animate-spin" />
          ) : (
            <MapPin className="h-4 w-4" />
          )}
          {gettingFreshGps
            ? "Locating…"
            : hasGps
              ? "Update GPS"
              : "Get GPS"}
        </Button>
      </section>

      <GpsAccuracyModal
        open={gpsModalOpen}
        onOpenChange={(o) => {
          setGpsModalOpen(o);
          if (!o) setGpsModalSeed(null);
        }}
        initialLat={gpsModalSeed?.lat ?? local.lat ?? pil.lat}
        initialLng={gpsModalSeed?.lng ?? local.lng ?? pil.lng}
        ordinal={local.ordinal}
        onConfirm={handleConfirmGps}
        isSaving={updateGps.isPending}
        userPos={userPos ?? null}
      />

      {/* 2. Pole photos */}
      <section className="space-y-3">
        <div className="space-y-0.5">
          <div className="flex items-center gap-1.5">
            <Label className="text-sm font-semibold">
              2. Pole / mounting-point photos
            </Label>
            <FieldInfoButton {...PIL_FIELD_HELP.polePhotos} />
          </div>
          <p className="text-xs text-muted-foreground">All 3 required</p>
        </div>
        <div className="grid grid-cols-3 gap-2">
          <SinglePhotoSlot
            label="Wide view"
            photo={poleWidePhoto}
            onCapture={(b64) => handlePoleUpload("pole_wide", b64)}
            onDelete={handlePoleDelete}
            disabled={isDone}
          />
          <SinglePhotoSlot
            label="Side / profile"
            photo={poleSidePhoto}
            onCapture={(b64) => handlePoleUpload("pole_side", b64)}
            onDelete={handlePoleDelete}
            disabled={isDone}
          />
          <SinglePhotoSlot
            label="Base / footer"
            photo={poleBasePhoto}
            onCapture={(b64) => handlePoleUpload("pole_base", b64)}
            onDelete={handlePoleDelete}
            disabled={isDone}
          />
        </div>
      </section>

      {/* 3. General comments */}
      <section className="space-y-2">
        <div className="flex items-center gap-1.5">
          <Label className="text-sm font-semibold">3. General comments</Label>
          <FieldInfoButton {...PIL_FIELD_HELP.generalComments} />
        </div>
        <Textarea
          value={local.general_comments ?? ""}
          onChange={(e) => setLocal((l) => ({ ...l, general_comments: e.target.value }))}
          onBlur={(e) => {
            if (e.target.value !== (pil.general_comments ?? "")) {
              commit({ general_comments: e.target.value });
            }
          }}
          placeholder="Anything else worth noting…"
          rows={3}
          disabled={isDone}
        />
      </section>

      {/* 4. Mark done — 3-button layout (section G):
          Row 1: full-width primary "Mark as done" / "Spot marked done".
          Row 2: 50/50 split — Edit + (Reject for admin-placed | Delete for
          mapper-placed). The destructive button never sits beside the
          primary CTA on the same row, which prevents accidental taps. */}
      <div className="pt-2 space-y-2">
        {!canMarkDone && !isDone && (
          <p className="text-xs text-muted-foreground">
            Capture GPS coordinates and all 3 pole photos to finish.
          </p>
        )}

        {/* Row 1: primary action — always full width */}
        <Button
          type="button"
          onClick={() => markDone.mutate({ token, pilId: local.id })}
          disabled={!canMarkDone || markDone.isPending || isDone}
          className="w-full min-h-12 text-base"
          size="lg"
        >
          {isDone ? (
            <>
              <Check className="h-4 w-4" />
              Spot marked done
            </>
          ) : markDone.isPending ? (
            <>
              <Loader2 className="h-4 w-4 animate-spin" />
              Marking…
            </>
          ) : (
            "Mark as done"
          )}
        </Button>

        {/* Row 2: secondary actions — 50/50 split.
            "Edit" only renders once the spot is done (the primary CTA flips
            to "Spot marked done", so we expose a way back to editing).
            Otherwise we hide it so the destructive button gets the full
            second row width. */}
        {(isDone || canReject || canDelete) && (
          <div className="grid grid-cols-2 gap-2">
            {isDone ? (
              <Button
                type="button"
                variant="outline"
                onClick={() => reopen.mutate({ token, pilId: local.id })}
                disabled={reopen.isPending}
                className="min-h-12 gap-2"
                size="lg"
              >
                {reopen.isPending ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <Pencil className="h-4 w-4" />
                )}
                Edit
              </Button>
            ) : (
              <span aria-hidden="true" />
            )}

            {canReject && (
              <Button
                type="button"
                variant="outline"
                onClick={() => setRejectOpen(true)}
                disabled={reject.isPending}
                className="min-h-12 gap-2 border-destructive/40 text-destructive hover:bg-destructive/10 hover:text-destructive"
                size="lg"
              >
                {reject.isPending ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <Ban className="h-4 w-4" />
                )}
                Reject
              </Button>
            )}
            {canDelete && (
              <Button
                type="button"
                variant="outline"
                onClick={() => setDeleteOpen(true)}
                disabled={deleteMapperPIL.isPending}
                className="min-h-12 gap-2 border-destructive/40 text-destructive hover:bg-destructive/10 hover:text-destructive"
                size="lg"
              >
                {deleteMapperPIL.isPending ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <Trash2 className="h-4 w-4" />
                )}
                Delete
              </Button>
            )}
          </div>
        )}
      </div>

      <AlertDialog open={rejectOpen} onOpenChange={setRejectOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Reject this installation spot?</AlertDialogTitle>
            <AlertDialogDescription>
              Use this when the pre-placed spot can't be installed on-site (e.g. the satellite image is outdated, the object is gone, or access is blocked). You can un-reject later if needed.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <div className="space-y-2">
            <Label className="text-sm">Reason (optional)</Label>
            <Textarea
              value={rejectReason}
              onChange={(e) => setRejectReason(e.target.value)}
              placeholder="e.g. Pole no longer exists"
              rows={3}
            />
          </div>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => setRejectReason("")}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                reject.mutate({ token, pilId: local.id, reason: rejectReason });
                setRejectReason("");
              }}
              className="bg-destructive text-white hover:bg-destructive/90"
            >
              Reject
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={deleteOpen} onOpenChange={setDeleteOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete this installation spot?</AlertDialogTitle>
            <AlertDialogDescription>
              This permanently removes the spot and any photos you took for it. This can't be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => deleteMapperPIL.mutate({ token, pilId: local.id })}
              className="bg-destructive text-white hover:bg-destructive/90"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

